import React, { useEffect, useMemo, useState } from "react";
import type { OrderItem } from "../OrderItemRow";
import { TicketCheckIcon, Loader2, UserPlus, X, User } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import CustomerSelectionModal from "../CustomerSelectionModal";
import type { Customer } from "../../types/store";
import { useCustomers } from "../../hooks/useCustomers";
import { receiptStyle } from "../../lib/receiptStyle";
import { createSale } from "../../slices/salesSlice";
import { fetchProducts } from "../../slices/productSlice";
import { fetchCustomers } from "../../slices/customerSlice";
import { saveOfflineSale } from "../../lib/offlineSales";
import { toast } from "react-toastify";
import OrderCompletionModal from "../OrderCompletionModal";
// import { createSale } from "../../slices/salesSlice";
// import { saveOfflineSale } from "../../lib/offlineSales";

interface OrderConfirmationProps {
  items: OrderItem[];
  onConfirm: () => void;
  onClose: () => void;
  storeId: string;
}

const OrderConfirmation: React.FC<OrderConfirmationProps> = ({
  items,
  onConfirm,
  onClose,
  storeId,
}) => {
  const [amountReceived, setAmountReceived] = useState<number>(0);
  const [paymentMethod, setPaymentMethod] = useState<string>("Cash");
  const [openCustomerModal, setOpenAddCustomerModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [allowGuestCheckout, setAllowGuestCheckout] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [saleComplete, setSaleComplete] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const userDetails = useAppSelector((state) => state.auth.user);
  const dispatch = useAppDispatch();
  const { customers } = useCustomers();

  // Compute total amount due
  const totalAmount = useMemo(
    () =>
      items.reduce(
        (sum, item) => sum + item.quantity * Number(item.selling_price),
        0
      ),
    [items]
  );

  const change = amountReceived - totalAmount;

  //   const handleConfirmOrder = async (actionType: "print" | "email") => {
  //   const payload = {
  //         customer_id: selectedCustomer?.id || null,
  //         new_customer: selectedCustomer
  //         ? {
  //             firstname: selectedCustomer.firstname,
  //             lastname: selectedCustomer.lastname,
  //             email: selectedCustomer.email,
  //             phone_number: selectedCustomer.phone_number,
  //         }
  //         : null,
  //         sale: {
  //             cashier_id: userDetails?.id as string,
  //             business_name: userDetails?.Business.name as string,
  //             store_id: storeId,
  //             payment_method: paymentMethod,
  //             amount_received: parseFloat(String(amountReceived)).toString(),
  //             items: items.map((item) => ({
  //                 product_id: item.id,
  //                 quantity: item.quantity,
  //             })),
  //         },
  //     };

  //   if (isOnline) {
  //     try {
  //       await dispatch(createSale(payload)).unwrap();
  //       if (actionType === "print") handlePrintReceipt();
  //       if (actionType === "email") alert("Receipt sent to email!");
  //     } catch (err) {
  //       console.error("Sale failed, saving locally...");
  //       await saveOfflineSale(payload);
  //     }
  //   } else {
  //     await saveOfflineSale(payload);
  //     alert("Offline mode: sale saved locally.");
  //   }
  // };


  const handleSave = async () => {
    // 🧩 Check if no customer is selected and user hasn't chosen to skip
    if (!selectedCustomer && !allowGuestCheckout) {
      alert("Please select a customer or choose to continue without one before proceeding.");
      setOpenAddCustomerModal(true);
      return;
    }

    setIsProcessing(true);

    // 🧾 Build the payload
    const isNewCustomer = !!selectedCustomer && !selectedCustomer.id;

    const payload = {
      sale:
      {
        // Only send customer_id when the customer already exists in the DB
        customer_id: selectedCustomer?.id || null,
        // Only send new_customer when it's a brand-new customer (no id yet).
        // Also normalise the phone field: CustomerSelectionModal uses `phone`,
        // the Customer type uses `phone_number`.
        new_customer: isNewCustomer
          ? {
            firstname: selectedCustomer!.firstname,
            lastname: selectedCustomer!.lastname,
            email: selectedCustomer!.email,
            phone_number: (selectedCustomer as any).phone_number
              ?? (selectedCustomer as any).phone
              ?? null,
            store_id: storeId || null,
          }
          : null,
        cashier_id: userDetails?.id as string,
        business_name: userDetails?.Business.name as string,
        store_id: storeId,
        payment_method: paymentMethod,
        amount_received: parseFloat(String(amountReceived)).toString(),
        items: items.map((item) => ({
          product_id: item.id,
          quantity: item.quantity,
        }))
      }
    };

    console.log("Sales payload:", payload);

    // 💾 Open customer modal only if user hasn’t confirmed customer selection
    if (!selectedCustomer && !allowGuestCheckout) {
      setOpenAddCustomerModal(true);
      return;
    }

    if (isOnline) {
      try {
        await dispatch(createSale(payload)).unwrap();
        // Refetch products to update stock levels
        dispatch(fetchProducts());
        // Refetch customers if a new customer was created during this sale
        if (isNewCustomer) {
          dispatch(fetchCustomers());
        }
      } catch (err) {
        console.error("Sale failed, saving locally...");
        toast.error("Sale failed, saving locally...");
        await saveOfflineSale(payload);
      } finally {
        setIsProcessing(false);
        setSaleComplete(true);
      }
    } else {
      await saveOfflineSale(payload);
      setIsProcessing(false);
      setSaleComplete(true);
    }

    // disp
    // Here you can dispatch your thunk or send to backend
  };


  const handlePrintReceipt = () => {
    if (change < 0) {
      return alert("Insufficient amount received");
    }
    const receiptWindow = window.open("", "PRINT", "height=600,width=800");
    if (receiptWindow) {
      const date = new Date();
      const day = date.toDateString();
      const hour = date.getHours();
      const minute = date.getMinutes();
      const seconds = date.getSeconds();

      receiptWindow.document.write("<html><head><title>Receipt</title>");
      receiptWindow.document.write(receiptStyle);
      receiptWindow.document.write("</head><body>");
      receiptWindow.document.write(`<h1>${userDetails?.Business.name}</h1>`);
      receiptWindow.document.write(`<h2>Sales Receipt</h2>`);
      receiptWindow.document.write(`<h4>${day} - ${hour}:${minute}:${seconds}</h4>`);

      // 🧾 Item Listing
      items.forEach((item) => {
        const total = item.quantity * Number(item.selling_price);
        receiptWindow.document.write(`
        <div class="item">
            <div class="item-name">
            ${item.name}
            <span class="item-price">₵${Number(item.selling_price).toFixed(2)}</span>
            </div>
            <div class="details">Qty: ${item.quantity}</div>
            <div class="details">Total: ₵${total.toFixed(2)}</div>
        </div>
        `);
      });

      receiptWindow.document.write(`
        <div class="summary">
        <p><span>Total:</span> <span>₵${totalAmount.toFixed(2)}</span></p>
        <p><span>Amount Received:</span> <span>₵${amountReceived.toFixed(2)}</span></p>
        <p><span>Change:</span> <span>₵${change.toFixed(2)}</span></p>
        <p><span>Payment Method:</span> <span>${paymentMethod}</span></p>
        </div>
    `);

      receiptWindow.document.write(`
        <div class="footer">
            &copy; ${new Date().getFullYear()} StorePilot. All rights reserved.
            Learn more at domitech.co
        </div>
    `)

      receiptWindow.document.write("</body></html>");
      receiptWindow.document.close();
      receiptWindow.print();
    }
  };

  const handleEmailReceipt = () => {
    if (change < 0) {
      return alert("Insufficient amount received");
    }
    setOpenAddCustomerModal(true);
    // You can later integrate this with a backend endpoint to send email receipts.
    console.log("Customer: ", selectedCustomer);
    // alert("Receipt sent via email!");
  };

  return (
    <div className="bg-white shadow-lg rounded-2xl p-6 w-full mx-auto space-y-4">
      <h1 className="text-xl text-primary font-semibold border-b pb-2">Order Confirmation</h1>

      <div className="space-y-8 text-left">
        <div>
          <label className="form-label">Total Amount:</label>
          <div className={`form-input ${change < 0 ? "text-red-500" : "text-green-600"}`}>
            GHC {totalAmount.toFixed(2)}
          </div>
        </div>

        <div>
          <label className="block text-sm mb-1">Amount Received</label>
          <input
            type="number"
            value={amountReceived}
            onChange={(e) => setAmountReceived(parseFloat(e.target.value) || 0)}
            className="border rounded-md w-full p-2"
            placeholder="Enter amount received"
          />
        </div>

        <div>
          <label className="form-label">Change</label>
          <div className={`form-input ${change < 0 ? "text-red-500" : "text-green-600"}`}>
            GHC{change.toFixed(2)}
          </div>
        </div>

        {/* ── Customer ── */}
        <div>
          <label className="block text-sm mb-2 font-medium text-gray-700">Customer</label>

          {selectedCustomer ? (
            <div className="flex items-start justify-between rounded-lg border border-primary/30 bg-primary/5 p-3">
              <div className="flex items-start gap-2">
                <User size={16} className="text-primary mt-0.5 shrink-0" />
                <div className="text-sm space-y-0.5">
                  <p className="font-medium text-gray-800">
                    {[selectedCustomer.firstname, (selectedCustomer as any).othername, selectedCustomer.lastname]
                      .filter(Boolean)
                      .join(" ")}
                  </p>
                  {selectedCustomer.email && (
                    <p className="text-gray-500">{selectedCustomer.email}</p>
                  )}
                  {((selectedCustomer as any).phone_number ?? (selectedCustomer as any).phone) && (
                    <p className="text-gray-500">
                      {(selectedCustomer as any).phone_number ?? (selectedCustomer as any).phone}
                    </p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedCustomer(null)}
                className="text-gray-400 hover:text-red-500 transition-colors ml-2 shrink-0"
                aria-label="Remove customer"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setOpenAddCustomerModal(true)}
              className="w-full flex items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 p-3 text-sm text-gray-500 hover:border-primary hover:text-primary transition-colors"
            >
              <UserPlus size={16} />
              <span>Select or add customer</span>
            </button>
          )}
        </div>

        <div className="flex flex-col">
          <label className="block text-sm mb-2 font-medium text-gray-700">
            Payment Method
          </label>

          <div className="grid grid-cols-2 gap-2">
            {[
              "Cash",
              "Credit/Debit Card",
              "Mobile Money",
              "Bank Transfer",
            ].map((method) => (
              <label
                key={method}
                className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors 
                    ${paymentMethod === method
                    ? "bg-blue-100 border-blue-500"
                    : "bg-gray-50 hover:bg-gray-100"
                  }`}
              >
                <input
                  type="radio"
                  name="payment"
                  value={method}
                  checked={paymentMethod === method}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{method}</span>
              </label>
            ))}
          </div>

          <label className="flex items-center gap-2 mt-3">
            <input
              type="checkbox"
              checked={allowGuestCheckout}
              onChange={(e) => setAllowGuestCheckout(e.target.checked)}
            />
            <span className="text-sm text-gray-600">Proceed without saving customer details</span>
          </label>
        </div>

      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          onClick={onClose}
          disabled={isProcessing}
          className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>

        <button
          onClick={handleSave}
          disabled={isProcessing}
          className="px-3 py-2 text-sm bg-primary text-white rounded-md hover:bg-blue-900 flex gap-2 items-center disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {isProcessing ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <TicketCheckIcon size={12} />
          )}
          <span>{isProcessing ? "Processing..." : "Complete Purchase"}</span>
        </button>
      </div>

      {openCustomerModal &&
        <CustomerSelectionModal
          customers={customers}
          onSelect={(cust) => {
            setSelectedCustomer(cust as Customer);
            setOpenAddCustomerModal(false);
          }}
          onAddNew={(cust) => {
            // dispatch(addNewCustomerThunk(cust)); // if using Redux or API call
            setSelectedCustomer(cust as Customer)
            console.log("Customer:", cust)
          }}
          onClose={() => setOpenAddCustomerModal(false)}
        />
      }

      {saleComplete && (
        <OrderCompletionModal
          onClose={() => {
            setSaleComplete(false);
            onConfirm();
            onClose();
          }}
          onEmail={handleEmailReceipt}
          onPrint={handlePrintReceipt}
        />
      )}
    </div>
  );
};

export default OrderConfirmation;
